import React, { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { formatSessionDate } from '../../../utils/formatDate';
import CurrencyAmount from '../../../components/ui/CurrencyAmount/CurrencyAmount';

/**
 * Daily bar chart for a single month.
 *
 * Design intent:
 *   - one bar per day (empty days render as a zero bar so the axis
 *     stays stable across months of different lengths)
 *   - x-axis shows the day-of-month number; the tooltip carries the
 *     full date and per-day stats
 *   - respects `prefers-reduced-motion`: animations are disabled at
 *     the media query, not per-render, so a mid-render change to the
 *     user's OS setting is picked up on the next mount
 *
 * Recharts is SVG-based, so we can drive colours off CSS custom
 * properties (`var(--gc-brand)`) and the whole chart re-tints when
 * `data-theme` swaps.
 */
function TooltipContent({ active, payload, currency }) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="gc-insights-tooltip" role="tooltip">
      <div className="gc-insights-tooltip-date">{formatSessionDate(row.date + 'T12:00:00Z')}</div>
      <div className="gc-insights-tooltip-row">
        <span>{Number(row.energyKWh).toFixed(2)} kWh</span>
      </div>
      <div className="gc-insights-tooltip-row">
        <span>
          {row.sessionCount === 1 ? '1 session' : `${row.sessionCount} sessions`}
        </span>
      </div>
      {row.totalCost > 0 && (
        <div className="gc-insights-tooltip-row">
          <span>
            You paid{' '}
            <CurrencyAmount value={row.totalCost} currency={currency} />
          </span>
        </div>
      )}
    </div>
  );
}

function MonthChart({ daily, currency = 'BBD' }) {
  // Detect prefers-reduced-motion once at mount. Doesn't hot-swap; a
  // page reload picks up any change. The tradeoff is acceptable for a
  // chart animation and keeps this component render-cheap.
  const reduceMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch { return false; }
  }, []);

  const chartData = useMemo(
    () => (Array.isArray(daily) ? daily : []).map((d) => ({
      ...d,
      day: Number(d.date.slice(-2)),
    })),
    [daily],
  );

  const hasAny = chartData.some((d) => d.energyKWh > 0 || d.sessionCount > 0);

  return (
    <div
      className="gc-insights-chart"
      role="img"
      aria-label={
        hasAny
          ? 'Daily energy consumption for the selected month'
          : 'No charging activity for the selected month'
      }
    >
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={chartData}
          margin={{ top: 8, right: 8, bottom: 4, left: -12 }}
          barCategoryGap="18%"
        >
          <CartesianGrid
            vertical={false}
            stroke="var(--gc-divider)"
            strokeDasharray="0"
          />
          <XAxis
            dataKey="day"
            tickLine={false}
            axisLine={{ stroke: 'var(--gc-border)' }}
            tick={{ fontSize: 11, fill: 'var(--gc-text-muted)' }}
            interval="preserveStartEnd"
            minTickGap={12}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: 'var(--gc-text-muted)' }}
            width={40}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: 'var(--gc-surface-muted)' }}
            content={<TooltipContent currency={currency} />}
          />
          <Bar
            dataKey="energyKWh"
            fill="var(--gc-brand)"
            radius={[4, 4, 0, 0]}
            isAnimationActive={!reduceMotion}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default MonthChart;
